import Analytics from '@opencare/analytics';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ValidationRulePreset } from 'common/Validator/ValidationRules';
import { createImmutableFormValidatorConfig, FieldValidation, useFormValidator } from 'common/Validator/Validator';

import { CreatePartnerPatient } from 'connect/saga/handlers/partner/create-partner-patient';
import { selectDataPartnerPatient, selectDataUser } from 'connect/selectors';
import { Input, InputType } from 'pages/checkout/components/common/Input';
import { FormError } from 'pages/checkout/components/common/input-styled-components';
import { Form } from 'pages/checkout/components/common/layout-styled-components';
import { CheckoutStateHandler } from 'pages/checkout/connect/state-handlers';
import { CheckoutState } from 'pages/checkout/connect/types';
import { formatPhoneNumberForApi } from 'pages/checkout/utils/phoneUtils';
import { PartnerKey } from 'pages/partner/constants';
import { usePatch } from 'utils/customHooks/usePatch';

import { FormFieldsWrapper, SubmitButton } from '../../Account-styled-components';

const FORM_VALIDATOR_CONFIG = createImmutableFormValidatorConfig<void>([
  { field: 'email', preset: ValidationRulePreset.email },
  { field: 'phone', preset: ValidationRulePreset.phone },
  { field: 'phone', preset: ValidationRulePreset.phoneCountryCode },
]);

interface FormValidationResult {
  allValid?: boolean;
  email?: FieldValidation;
  phone?: FieldValidation;
  phoneCountryCode?: FieldValidation;
}

const analytics = new Analytics();

/**
 * A component for creating a partner patient account.
 */
export const PartnerPatient = () => {
  const dispatch = useDispatch();

  const currentUser = useSelector(selectDataUser);

  const creationState = {
    patient: useSelector(selectDataPartnerPatient),
    error: useSelector(CreatePartnerPatient.error!.selector),
    isLoading: useSelector(CreatePartnerPatient.loading!.selector),
  };

  // Input fields state.
  const [email, setEmail] = useState<string>(creationState.patient?.email ?? '');
  const [phone, setPhone] = useState<string>(creationState.patient?.mobilePhone ?? '');

  // Form validation.
  const formValidator = useFormValidator(FORM_VALIDATOR_CONFIG);

  const [formValidationResult, setFormValidationResult] = useState<FormValidationResult>({});
  const [shouldShowValidationErrors, setShouldShowValidationErrors] = useState<boolean>(false);

  const formValidationState = useMemo(
    () => ({ email, phone }),
    [email, phone],
  );

  const emailValidationError = useMemo(
    () => shouldShowValidationErrors ? formValidationResult.email?.message : undefined,
    [shouldShowValidationErrors, formValidationResult],
  );
  const phoneValidationError = useMemo(
    () => shouldShowValidationErrors
      ? formValidationResult.phone?.message || formValidationResult.phoneCountryCode?.message
      : undefined,
    [shouldShowValidationErrors, formValidationResult],
  );

  const validateForm = useCallback(() => {
    setFormValidationResult(
      formValidator.validate(formValidationState),
    );
  }, [formValidator, formValidationState]);

  // Validate form on the state change.
  useEffect(() => {
    validateForm();
  }, [formValidationState, validateForm]);

  const patchCheckout = usePatch<CheckoutState>({
    stateHandler: CheckoutStateHandler,
  });

  const handleEmailInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  }, []);

  const handlePhoneInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(event.target.value);
  }, []);

  const handleFormSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setShouldShowValidationErrors(true);

    if (!formValidationResult.allValid) {
      return;
    }

    const partnerKey = currentUser?.partner?.key;
    // Healthgrades partner patient should still pass the phone verification.
    const isPhoneVerified = partnerKey !== PartnerKey.HEALTHGRADES;

    const patientData = {
      email,
      isEmailVerified: true,
      phone: formatPhoneNumberForApi(phone) as string,
      isPhoneVerified,
    };

    patchCheckout({
      account: {
        email: patientData.email,
        phone: patientData.phone,
      },
    });

    analytics.trackForm({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'partner-checkout-info',
      name: 'checkout',
      section: 'account',
      content: {
        email: patientData.email,
        phone: patientData.phone,
        partner_key: partnerKey,
      },
    });

    dispatch(
      CreatePartnerPatient.createAction(patientData),
    );
  }, [
    dispatch,
    patchCheckout,
    formValidationResult,
    currentUser,
    email,
    phone,
  ]);

  return (
    <>
      {creationState.error && !creationState.isLoading && (
        <FormError>{creationState.error}</FormError>
      )}

      <Form onSubmit={handleFormSubmit}>
        <FormFieldsWrapper>
          <Input
            type={InputType.EMAIL}
            name="email"
            placeholder="Enter patient's email"
            aria-label="Email"
            value={email}
            onChange={handleEmailInputChange}
            errorMessage={emailValidationError}
            disabled={creationState.isLoading}
          />
          <Input
            type={InputType.TEL}
            name="phone"
            placeholder="Enter patient's phone"
            aria-label="Mobile Phone"
            value={phone}
            onChange={handlePhoneInputChange}
            errorMessage={phoneValidationError}
            disabled={creationState.isLoading}
          />
        </FormFieldsWrapper>

        <SubmitButton type="submit" $loading={creationState.isLoading}>
          Continue Booking
        </SubmitButton>
      </Form>
    </>
  );
};
